import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-neutral-900 mb-8 animate-fade-in">Privacy Policy</h1>
          <div className="text-xl text-neutral-600 space-y-2 animate-fade-in">
            <p><strong className="text-neutral-800">App Name:</strong> Tinnitus Tracker</p>
            <p><strong className="text-neutral-800">Developer:</strong> SaeHyoung Lee</p>
            <p><strong className="text-neutral-800">Effective Date:</strong> August 10, 2025</p>
          </div>
        </div>

        {/* Privacy Statement Banner */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl p-8 mb-12 text-center shadow-soft">
          <h2 className="text-3xl font-bold mb-4">Your Privacy is Our Priority</h2>
          <p className="text-xl text-primary-100">Tinnitus Tracker collects ZERO personal data. Everything stays on your device.</p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-soft p-12 space-y-12 border border-neutral-200">
          
          {/* Section 1: No Data Collection */}
          <section>
            <h2 className="text-3xl font-bold text-neutral-900 mb-6 flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-7 h-7 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                </svg>
              </div>
              We Do NOT Collect Any Personal Data
            </h2>
            <div className="text-neutral-700 space-y-4 ml-16 text-lg">
              <p>• <strong className="text-neutral-900">No user accounts or registration required</strong> - You can use Tinnitus Tracker immediately without creating any accounts</p>
              <p>• <strong className="text-neutral-900">No email addresses, names, or personal identifiers collected</strong> - We never ask for or store any personal information</p>
              <p>• <strong className="text-neutral-900">No data ever leaves your device</strong> - All app functionality operates completely offline with zero internet connectivity required</p>
            </div>
          </section>

          {/* Section 2: Local Storage */}
          <section>
            <h2 className="text-3xl font-bold text-neutral-900 mb-6 flex items-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-7 h-7 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                </svg>
              </div>
              Local Storage Only
            </h2>
            <div className="text-neutral-700 space-y-4 ml-16 text-lg">
              <p>• <strong className="text-neutral-900">All diary entries, frequency matching sessions, and preferences are stored locally</strong> on your device using Apple's secure Core Data framework</p>
              <p>• <strong className="text-neutral-900">Data is protected by your device's security</strong> including Face ID, Touch ID, or passcode - no one can access your data without your device credentials</p>
              <p>• <strong className="text-neutral-900">If you delete the app, all data is permanently removed</strong> - There are no backups stored anywhere else</p>
            </div>
          </section>

          {/* Section 3: No Third-Party Services */}
          <section>
            <h2 className="text-3xl font-bold text-neutral-900 mb-6 flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-7 h-7 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              No Third-Party Services
            </h2>
            <div className="text-neutral-700 space-y-4 ml-16 text-lg">
              <p>• <strong className="text-neutral-900">No analytics services</strong> (no Google Analytics, Firebase, or similar tracking)</p>
              <p>• <strong className="text-neutral-900">No advertising networks</strong> - The app is completely ad-free</p>
              <p>• <strong className="text-neutral-900">No crash reporting services</strong> - We don't collect crash reports or usage statistics</p>
              <p>• <strong className="text-neutral-900">No backend servers or cloud storage</strong> - The app functions entirely offline</p>
              <p>• <strong className="text-neutral-900">Completely offline functionality</strong> - Internet connection is never required</p>
            </div>
          </section>

          {/* Section 4: Audio Permissions */}
          <section>
            <h2 className="text-3xl font-bold text-neutral-900 mb-6 flex items-center">
              <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-7 h-7 text-accent-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM15.657 6.343a1 1 0 011.414 0A9.972 9.972 0 0119 12a9.972 9.972 0 01-1.929 5.657 1 1 0 11-1.414-1.414A7.971 7.971 0 0017 12c0-1.594-.471-3.082-1.343-4.343a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 12a5.984 5.984 0 01-.757 2.829 1 1 0 01-1.415-1.414A3.987 3.987 0 0013 12a3.988 3.988 0 00-.172-1.243 1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              Audio Permissions
            </h2>
            <div className="text-neutral-700 space-y-4 ml-16 text-lg">
              <p>• <strong className="text-neutral-900">We request audio session permissions solely to generate therapeutic sounds</strong> for frequency matching and sound therapy</p>
              <p>• <strong className="text-neutral-900">No audio recording takes place</strong> - The app only outputs sound, never records</p>
              <p>• <strong className="text-neutral-900">Microphone access is never requested or used</strong> - We have no need to access your microphone</p>
            </div>
          </section>

          {/* Section 5: Data You Control */}
          <section>
            <h2 className="text-3xl font-bold text-neutral-900 mb-6 flex items-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-7 h-7 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
              </div>
              Data You Control
            </h2>
            <div className="text-neutral-700 space-y-4 ml-16 text-lg">
              <p>• <strong className="text-neutral-900">Export your diary data anytime</strong> for sharing with your healthcare provider - data remains in your control</p>
              <p>• <strong className="text-neutral-900">Delete individual entries or all data</strong> through the app's settings</p>
              <p>• <strong className="text-neutral-900">Uninstalling the app removes all associated data</strong> - no traces left behind</p>
            </div>
          </section>

          {/* Section 6: Children's Privacy */}
          <section>
            <h2 className="text-3xl font-bold text-neutral-900 mb-6 flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-7 h-7 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                </svg>
              </div>
              Children's Privacy
            </h2>
            <div className="text-neutral-700 space-y-4 ml-16 text-lg">
              <p>• <strong className="text-neutral-900">We do not knowingly collect data from anyone</strong> - regardless of age</p>
              <p>• <strong className="text-neutral-900">The app can be safely used by users of all ages</strong> - no age restrictions due to data collection</p>
              <p>• <strong className="text-neutral-900">Parents can trust that no personal information is gathered</strong> from children or adults</p>
            </div>
          </section>

          {/* Section 7: Contact Information */}
          <section>
            <h2 className="text-3xl font-bold text-neutral-900 mb-6 flex items-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-7 h-7 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              Contact Information
            </h2>
            <div className="text-neutral-700 space-y-4 ml-16 text-lg">
              <p>• <strong className="text-neutral-900">General Support:</strong> <a href="mailto:samchris@naver.com" className="text-primary-600 hover:text-primary-700 underline transition-colors">samchris@naver.com</a></p>
              <p>• <strong className="text-neutral-900">Privacy Questions:</strong> <a href="mailto:samchris@naver.com" className="text-primary-600 hover:text-primary-700 underline transition-colors">samchris@naver.com</a></p>
            </div>
          </section>

        </div>

        {/* Final Privacy Assurance */}
        <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 text-white rounded-2xl p-10 mt-12 text-center shadow-soft">
          <h3 className="text-2xl font-bold mb-4">Complete Privacy Guarantee</h3>
          <p className="text-neutral-300 text-lg leading-relaxed max-w-3xl mx-auto">Tinnitus Tracker is designed with privacy-by-design principles. We cannot access your data because we never collect it in the first place. Your health information remains exclusively yours.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;